import { useEffect, useState } from "react";
import { Empty, Spin, Timeline, Tag, Collapse, Table } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { GetAuditoriaClinicaHistorial } from "../../Service/Service.auditoriaClinica";

const { Panel } = Collapse;

// AUDITORIA-CLINICA-FASE-1 §20/21 -- historial de correcciones de una cita.
// Vacío es un resultado VÁLIDO (no un error): toda cita nueva/no corregida
// debe mostrar el mensaje explícito, nunca un estado de error/carga infinita.
//
// AUDITORIA-CLINICA-FASE-2: cada detalle de una correccion ahora muestra
// antes/despues en forma LEGIBLE (nunca el JSON crudo) -- una tabla
// campo/antes/despues para signos_vitales (puede tener varios campos
// numericos) y pares de linea "antes -> despues" para las demas secciones.
const CAMPOS_LABEL = {
    motivo: "Motivo",
    enfermedad_actual: "Enfermedad actual",
    plan_tratamiento: "Plan de tratamiento",
    nota_evolucion: "Nota de evolución",
    temperatura: "Temperatura",
    presion: "Presión",
    pulso: "Pulso",
    frecuencia_respiratoria: "Frecuencia respiratoria",
    perimetro_cefalico: "Perímetro cefálico",
    peso: "Peso",
    talla: "Talla",
    saturacion_oxigeno: "Saturación de oxígeno",
    imc: "IMC",
};
const fmtValor = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

const fmtDiagnostico = (d) => {
    if (!d) return "—";
    const codigo = d.codigo || "";
    const nombre = d.nombre || "";
    const etiqueta = [codigo, nombre].filter(Boolean).join(" - ");
    return etiqueta || "—";
};

// FASE 4 -- recetas: se muestran de forma clinica legible (medicamento,
// indicaciones, posologia...), nunca como JSON crudo, y con el numero de
// receta original y de reemplazo para que la cadena de versiones sea
// evidente.
const RECETA_CAMPOS = ["medicamentos", "indicaciones", "posologia", "cantidad", "dosis"];
const RECETA_CAMPOS_LABEL = {
    medicamentos: "Medicamento",
    indicaciones: "Indicaciones",
    posologia: "Posología",
    cantidad: "Cantidad",
    dosis: "Dosis",
};

const SECTION_LABEL = {
    motivo_consulta: "Motivo de consulta",
    signos_vitales: "Signos vitales",
    enfermedad_actual: "Enfermedad actual",
    plan_tratamiento: "Plan de tratamiento",
    diagnosticos: "Diagnósticos",
    nota_evolucion: "Nota de evolución",
    recetas: "Recetas",
};
const OPERATION_LABEL = {
    UPDATE: "Corregido",
    ADD: "Agregado",
    REMOVE: "Retirado",
    REPLACE: "Sustituida",
    VOID: "Anulada",
};
const panelHeader = (d) =>
    `${SECTION_LABEL[d.section] || d.section} — ${OPERATION_LABEL[d.operation] || d.operation}`;

function DetalleReceta({ detalle }) {
    const before = detalle.before_json || {};
    const after = detalle.after_json || {};
    const esVoid = detalle.operation === "VOID";

    return (
        <div data-testid="historial-receta">
            <div className="mb-2">
                <strong>{esVoid ? "Receta anulada" : "Receta sustituida"}</strong>
                {" "}
                <Tag color={esVoid ? "red" : "orange"}>
                    {esVoid ? "Anulada" : "Reemplazada"}
                </Tag>
            </div>
            <div className="mb-1 text-muted">Original: receta #{before.idreceta ?? detalle.entity_id}</div>
            {!esVoid && <div className="mb-2 text-muted">Nueva: receta #{after.idreceta ?? "—"}</div>}
            <Table
                size="small"
                pagination={false}
                rowKey="campo"
                dataSource={RECETA_CAMPOS.map((f) => ({
                    campo: RECETA_CAMPOS_LABEL[f],
                    original: fmtValor(before[f]),
                    nueva: esVoid ? "Anulada (sin reemplazo)" : fmtValor(after[f]),
                }))}
                columns={[
                    { title: "Campo", dataIndex: "campo", key: "campo" },
                    { title: "Original", dataIndex: "original", key: "original" },
                    { title: esVoid ? "Resultado" : "Nueva", dataIndex: "nueva", key: "nueva" },
                ]}
            />
        </div>
    );
}

function DetalleAntesDepues({ detalle }) {
    const before = detalle.before_json || {};
    const after = detalle.after_json || {};
    const campos = [...new Set([...Object.keys(before), ...Object.keys(after)])];

    if (detalle.section === "recetas") {
        return <DetalleReceta detalle={detalle} />;
    }

    // FASE 3: diagnosticos es una coleccion con diff ADD/REMOVE, nunca campo
    // escalar -- se muestra el diagnostico agregado/retirado de forma legible
    // (CIE + nombre), nunca el JSON crudo.
    if (detalle.section === "diagnosticos") {
        const esAdd = detalle.operation === "ADD";
        return (
            <div>
                <strong>{esAdd ? "Diagnóstico agregado" : "Diagnóstico retirado"}:</strong>{" "}
                <span className="text-muted">{esAdd ? "—" : fmtDiagnostico(detalle.before_json)}</span>{" → "}
                <span>{esAdd ? fmtDiagnostico(detalle.after_json) : "—"}</span>
            </div>
        );
    }

    if (detalle.section === "signos_vitales") {
        const data = campos.map((campo) => ({
            key: campo,
            campo: CAMPOS_LABEL[campo] || campo,
            antes: fmtValor(before[campo]),
            despues: fmtValor(after[campo]),
        }));
        return (
            <Table
                size="small"
                pagination={false}
                dataSource={data}
                columns={[
                    { title: "Campo", dataIndex: "campo", key: "campo" },
                    { title: "Antes", dataIndex: "antes", key: "antes" },
                    { title: "Después", dataIndex: "despues", key: "despues" },
                ]}
            />
        );
    }

    return (
        <div className="d-flex flex-column gap-1">
            {campos.map((campo) => (
                <div key={campo}>
                    <strong>{CAMPOS_LABEL[campo] || campo}:</strong>{" "}
                    <span className="text-muted">{fmtValor(before[campo])}</span>{" → "}
                    <span>{fmtValor(after[campo])}</span>
                </div>
            ))}
        </div>
    );
}
function AuditoriaHistorialCorrecciones({ idcita }) {
    const [loading, setLoading] = useState(true);
    const [correcciones, setCorrecciones] = useState([]);
    const [mensaje, setMensaje] = useState("");
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!idcita) return;
        let cancelado = false;
        (async () => {
            setLoading(true);
            const res = await GetAuditoriaClinicaHistorial(idcita);
            if (cancelado) return;
            if (res?.success) {
                setCorrecciones(res.data || []);
                setMensaje(res.message || "");
                setError(null);
            } else {
                setCorrecciones([]);
                setError(res?.message || "No se pudo obtener el historial de correcciones.");
            }
            setLoading(false);
        })();
        return () => { cancelado = true; };
    }, [idcita]);

    if (loading) return <Spin />;
    if (error) return <Empty description={error} />;
    if (correcciones.length === 0) {
        return <Empty description={mensaje || "Esta atención no registra correcciones de auditoría."} />;
    }

    return (
        <Timeline
            items={correcciones.map((c) => ({
                children: (
                    <div key={c.id}>
                        <div>
                            <strong>{c.reason_code}</strong>{" "}
                            <Tag>{c.created_at ? format(new Date(c.created_at), "dd/MM/yyyy HH:mm", { locale: es }) : "—"}</Tag>
                        </div>
                        <div className="text-muted">
                            {c.actor?.full_name || "Usuario desconocido"} — {c.justification}
                        </div>
                        {Array.isArray(c.detalles) && c.detalles.length > 0 && (
                            <Collapse size="small" className="mt-2">
                                {c.detalles.map((d) => (
                                    <Panel header={panelHeader(d)} key={d.id}>
                                        <DetalleAntesDepues detalle={d} />
                                        {/* WORKFLOW-44 §12/§23: cada cambio responde QUÉ había,
                                            QUÉ lo reemplazó, POR QUÉ, QUIÉN y CUÁNDO. `reason_code`/
                                            `justification` del DETALLE son la fuente clínica; el
                                            histórico F1-F4 no los tiene y cae al de la cabecera,
                                            etiquetado como tal (nunca se inventa una razón). */}
                                        <div className="mt-2 pt-2 border-top small" data-testid={`detalle-trazabilidad-${d.id}`}>
                                            <div>
                                                <strong>Razón:</strong>{" "}
                                                {d.reason_code || (
                                                    <>
                                                        {c.reason_code} <Tag>general de la corrección</Tag>
                                                    </>
                                                )}
                                            </div>
                                            <div>
                                                <strong>Justificación:</strong>{" "}
                                                {d.justification || c.justification || "—"}
                                            </div>
                                            <div>
                                                <strong>Auditor:</strong> {c.actor?.full_name || "Usuario desconocido"}
                                            </div>
                                            <div>
                                                <strong>Fecha del cambio:</strong>{" "}
                                                {c.created_at ? format(new Date(c.created_at), "dd/MM/yyyy HH:mm", { locale: es }) : "—"}
                                            </div>
                                        </div>
                                    </Panel>
                                ))}
                            </Collapse>
                        )}
                    </div>
                ),
            }))}
        />
    );
}

export default AuditoriaHistorialCorrecciones;
